import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
// Diamond color grades mapped to visual colors (D=colorless → Z=light yellow)
const COLOR_GRADES = {
    'D': '#f0f8ff', // Ice blue tint - Colorless
    'E': '#f5faff',
    'F': '#fafcff',
    'G': '#fffffe', // Near Colorless
    'H': '#fffef8',
    'I': '#fffdf2',
    'J': '#fffceb',
    'K': '#fffae0', // Faint Yellow
    'L': '#fff8d4',
    'M': '#fff5c4',
    'N': '#fff2b0', // Light Yellow
    'O': '#ffefa0',
    'P': '#ffec90',
    'Q': '#ffe980',
    'R': '#ffe670',
    'S': '#ffe360',
    'T': '#ffe050',
    'U': '#ffdd40',
    'V': '#ffda30',
    'W': '#ffd720',
    'X': '#ffd410',
    'Y': '#ffd100',
    'Z': '#ffce00',
};
const COLOR_CATEGORIES = {
    'D': 'Colorless',
    'E': 'Colorless',
    'F': 'Colorless',
    'G': 'Near Colorless',
    'H': 'Near Colorless',
    'I': 'Near Colorless',
    'J': 'Near Colorless',
    'K': 'Faint Yellow',
    'L': 'Faint Yellow',
    'M': 'Faint Yellow',
    'N': 'Very Light',
    'O': 'Very Light',
    'P': 'Very Light',
    'Q': 'Very Light',
    'R': 'Very Light',
    'S': 'Light',
    'T': 'Light',
    'U': 'Light',
    'V': 'Light',
    'W': 'Light',
    'X': 'Light',
    'Y': 'Light',
    'Z': 'Light',
};
const ColorFilterContainer = styled.div `
  margin-bottom: 20px;
`;
const FilterSectionTitle = styled.h4 `
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
  margin-top: 0;
`;
const ColorFilterGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));
  gap: 6px;
`;
const ColorSwatch = styled.div `
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: ${props => props.$color};
  margin-bottom: 4px;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
`;
const ColorPill = styled.button `
  padding: 6px 10px 8px;
  min-width: 52px;
  background: ${props => props.$disabled ? 'var(--bg-tertiary)' :
    props.$isActive ? 'var(--accent-gold-bg, rgba(153, 101, 21, 0.08))' : 'var(--bg-secondary)'};
  border: 1px solid ${props => props.$disabled ? 'var(--border-color)' :
    props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--border-color)'};
  color: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--text-secondary)'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  opacity: ${props => props.$disabled ? 0.4 : 1};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  &:hover {
    border-color: ${props => props.$disabled ? 'var(--border-color)' : 'var(--accent-gold-border, rgba(153, 101, 21, 0.25))'};
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 4px 8px rgba(0,0,0,0.1)'};
  }

  &:hover ${ColorSwatch} {
    transform: ${props => props.$disabled ? 'none' : 'scaleY(1.5)'};
  }

  .color-label {
    font-weight: 600;
    font-size: 13px;
  }

  .color-count {
    font-size: 10px;
    color: var(--text-tertiary);
  }
`;
const AllColorsPill = styled(ColorPill) `
  background: ${props => props.$isActive ? 'var(--accent-gold, #996515)' : 'var(--bg-secondary)'};
  color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  font-weight: 600;
  min-width: 70px;

  .color-label {
    color: ${props => props.$isActive ? '#fff' : 'var(--text-primary)'};
  }

  .color-count {
    color: ${props => props.$isActive ? 'rgba(255,255,255,0.8)' : 'var(--text-tertiary)'};
  }
`;
export const ColorFilter = ({ selectedColor, colors, onColorChange, getColorCount }) => {
    const totalCount = colors.reduce((sum, color) => sum + getColorCount(color), 0);
    return (_jsxs(ColorFilterContainer, { children: [_jsx(FilterSectionTitle, { children: "Diamond Color" }), _jsxs(ColorFilterGrid, { children: [_jsxs(AllColorsPill, { "$isActive": !selectedColor, onClick: () => onColorChange(''), title: "All Colors", children: [_jsx("span", { className: "color-label", children: "All" }), _jsx("span", { className: "color-count", children: totalCount })] }), colors.map(color => {
                        const count = getColorCount(color);
                        const isDisabled = count === 0;
                        const colorHex = COLOR_GRADES[color] || '#f5f5f5';
                        const category = COLOR_CATEGORIES[color] || '';
                        return (_jsxs(ColorPill, { "$isActive": selectedColor === color, "$disabled": isDisabled, "$colorGrade": color, onClick: () => !isDisabled && onColorChange(color), title: `Color ${color} - ${category} (${count} diamonds)`, children: [_jsx(ColorSwatch, { "$color": colorHex }), _jsx("span", { className: "color-label", children: color }), _jsx("span", { className: "color-count", children: count })] }, color));
                    })] })] }));
};
